import React, { useRef } from 'react'
import { InView } from 'react-intersection-observer'
import { GuildAccessNotch } from '@/molecules/GuildAccessNotch'
import { Thumbnail } from '@/molecules/Thumbnail'
import { ContentInteraction, useCardInteractions } from '@/services/Analytics/ContentInteractions'
import { useGuildMember } from '@/services/GuildUserService'
import { LinkViewModel } from '@/services/RenderService'
import { formatCloudinaryPath } from '@/utils/Cloudinary/cloudinary'
import { useSafeTrack } from '@/utils/analytics'

interface LandscapeLinkProps {
  link: LinkViewModel
  itemIndex?: number
  createInteraction?: (link: LinkViewModel, itemIndex: number) => ContentInteraction | null
  hoverStartTimeRef?: React.MutableRefObject<Map<string, number>>
  impressionTimeoutRef?: React.MutableRefObject<Map<string, NodeJS.Timeout>>
  hasTrackedImpression?: React.MutableRefObject<Set<string>>
}

export function LandscapeLink(props: LandscapeLinkProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const { isGuildMember, loading } = useGuildMember()
  const track = useSafeTrack()
  const {
    link,
    itemIndex = 0,
    createInteraction,
    hoverStartTimeRef,
    impressionTimeoutRef,
    hasTrackedImpression,
  } = props

  const contentId = link.id ?? ''

  const onClickTrack = React.useCallback(() => {
    if (link.track) track(link.track?.eventName, link.track?.payload)
  }, [link.track, track])

  const shouldUseInteractions = Boolean(
    createInteraction && hoverStartTimeRef && impressionTimeoutRef && hasTrackedImpression,
  )

  const defaultHoverStartTimeRef = useRef(new Map<string, number>())
  const defaultImpressionTimeoutRef = useRef(new Map<string, NodeJS.Timeout>())
  const defaultHasTrackedImpression = useRef(new Set<string>())

  const cardInteractionsHook = useCardInteractions({
    contentId,
    item: link,
    itemIndex,
    hoverStartTimeRef: hoverStartTimeRef ?? defaultHoverStartTimeRef,
    impressionTimeoutRef: impressionTimeoutRef ?? defaultImpressionTimeoutRef,
    hasTrackedImpression: hasTrackedImpression ?? defaultHasTrackedImpression,
    createInteraction: createInteraction ?? (() => null),
    onClickTrack,
  })

  const handleMouseEnter = shouldUseInteractions ? cardInteractionsHook.handleMouseEnter : undefined
  const handleMouseLeave = shouldUseInteractions ? cardInteractionsHook.handleMouseLeave : undefined
  const handleClick = shouldUseInteractions ? cardInteractionsHook.handleClick : onClickTrack
  const handleInViewChange = shouldUseInteractions ? cardInteractionsHook.handleInViewChange : undefined

  return (
    <InView onChange={handleInViewChange}>
      <div
        className="relative my-2 overflow-hidden rounded-lg duration-[400ms] ease-in-out hover:z-10 md:hover:scale-[1.05]"
        ref={cardRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Thumbnail
          showTitle={false}
          className="mr-4"
          href={link.linkUrl}
          cloudinaryImagePath={formatCloudinaryPath(link.imageUrl) as string}
          title={link.alt}
          includePlayCircle={false}
        />
        <GuildAccessNotch isInEarlyAccess={link.isInEarlyAccess} isGuildMember={isGuildMember} loading={loading} />
      </div>
    </InView>
  )
}
